<template>
  <aside class="multi-profile-body" id="multi-profile-report-capture">
    <div class="profile-area">
      <div class="profile-header">
        <div class="profile-header-left">
          <div class="profile-category">
            {{ siteName || sensorGroupMeta?.siteName || '-' }}
          </div>

          <div class="profile-title">
            {{ selectedName || sensorGroupMeta?.sensorGroupName || '다단센서 프로파일' }}
          </div>

          <div class="profile-sub">
            {{ profileDescription }}
          </div>
        </div>

        <div class="profile-header-right">
          <div class="axis-control-box">
            <span class="axis-label">X 좌표</span>

            <input
              v-model="axisControl.xMin"
              type="number"
              step="1"
              class="axis-input"
              placeholder="min"
              @input="applyChartScale"
            />

            <span class="axis-tilde">~</span>

            <input
              v-model="axisControl.xMax"
              type="number"
              step="1"
              class="axis-input"
              placeholder="max"
              @input="applyChartScale"
            />

            <span class="axis-label">Z 좌표</span>

            <input
              v-model="axisControl.zMin"
              type="number"
              step="1"
              class="axis-input"
              placeholder="min"
              @input="applyChartScale"
            />

            <span class="axis-tilde">~</span>

            <input
              v-model="axisControl.zMax"
              type="number"
              step="1"
              class="axis-input"
              placeholder="max"
              @input="applyChartScale"
            />

            <button
              class="axis-btn"
              type="button"
              @click="resetChartScale"
            >
              초기화
            </button>
          </div>

          <select
            v-model="selectedTime"
            class="time-select"
            @change="renderChart"
          >
            <option
              v-for="item in profileData"
              :key="item.measuredAt"
              :value="item.measuredAt"
            >
              {{ formatTime(item.measuredAt) }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="!currentProfile" class="profile-empty">
        표시할 다단센서 데이터가 없습니다.
      </div>

      <div v-else class="profile-content">
        <div class="chart-card">
          <div class="chart-title">
            {{ chartTitle }}
          </div>

          <div
            class="chart-wrap"
            :class="{ 'tn-chart-wrap': isTnProfile }"
          >
            <canvas ref="profileCanvas"></canvas>
          </div>
        </div>

        <div class="table-card">
          <div class="table-title-row">
            <div class="table-title">
              노드 데이터
            </div>

            <div
              v-if="minMaxSummary"
              class="minmax-summary"
            >
              최소값 :
              <strong>{{ formatNumber(minMaxSummary.min) }}</strong>,
              최대값 :
              <strong>{{ formatNumber(minMaxSummary.max) }}</strong>
            </div>
          </div>

          <div class="table-scroll">
            <table class="profile-table">
              <thead>
                <tr>
                  <th>순번</th>
                  <th>센서명</th>
                  <th>
                    {{
                      isTnProfile
                        ? 'Z 좌표(mm)'
                        : isRailProfile
                          ? '거리(m)'
                          : 'Depth'
                    }}
                  </th>

                  <th>Axis</th>
                  <th>원 측정값</th>
                  <th>초기치</th>

                  <th v-if="isTnProfile">
                    X 좌표(mm)
                  </th>

                  <th>
                    {{
                      isTnProfile
                        ? '변화량(mm)'
                        : isRailProfile
                          ? '변위(mm)'
                          : '변화량'
                    }}
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="(node, index) in tableNodes"
                  :key="`${node.sensorId || 'fixed'}-${index}`"
                >
                  <td>{{ index + 1 }}</td>
                  <td>{{ node.sensorName || '-' }}</td>
                  <td>
                    {{
                      formatNumber(
                        isTnProfile
                          ? node.z
                          : isRailProfile
                            ? node.depth
                            : node.depth
                      )
                    }}
                  </td>
                  <td>{{ node.axisType || '-' }}</td>
                  <td>{{ formatNumber(node.rawValue) }}</td>
                  <td>{{ formatNumber(node.baseValue) }}</td>
                  <td v-if="isTnProfile">{{ formatNumber(node.x) }}</td>
                  <td>{{ formatNumber(node.value) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
import { markRaw } from 'vue'
import { Chart, registerables } from 'chart.js'

Chart.register(...registerables)

const thresholdLinePlugin = {
  id: 'multiSensorThresholdLine',

  afterDatasetsDraw(chart) {
    const { ctx, chartArea, scales } = chart

    if (!ctx || !chartArea) return

    chart.data.datasets.forEach((dataset, index) => {
      if (dataset.customType !== 'threshold') return
      if (!chart.isDatasetVisible(index)) return

      const value = Number(dataset.thresholdValue)
      if (!Number.isFinite(value)) return

      const direction = dataset.thresholdDirection || 'vertical'
      const color = dataset.borderColor || '#64748b'

      ctx.save()
      ctx.beginPath()
      ctx.setLineDash([6, 4])
      ctx.lineWidth = 1.5
      ctx.strokeStyle = color

      if (direction === 'horizontal') {
        const yScale = scales.y
        if (!yScale) {
          ctx.restore()
          return
        }

        const y = yScale.getPixelForValue(value)
        if (y < chartArea.top || y > chartArea.bottom) {
          ctx.restore()
          return
        }

        ctx.moveTo(chartArea.left, y)
        ctx.lineTo(chartArea.right, y)
        ctx.stroke()

      } else {
        const xScale = scales.x
        if (!xScale) {
          ctx.restore()
          return
        }

        const x = xScale.getPixelForValue(value)
        if (x < chartArea.left || x > chartArea.right) {
          ctx.restore()
          return
        }

        ctx.moveTo(x, chartArea.top)
        ctx.lineTo(x, chartArea.bottom)
        ctx.stroke()
      }

      ctx.restore()
    })
  }
}

export default {
  name: 'MultiSensorProfileArea',

  props: {
    profileData: {
      type: Array,
      default: () => []
    },
    sensorGroupMeta: {
      type: Object,
      default: null
    },
    siteName: {
      type: String,
      default: ''
    },
    selectedName: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      chart: null,
      selectedTime: null,

      axisControl: this.createEmptyAxisControl()
    }
  },

  computed: {
    currentProfile() {
      if (!Array.isArray(this.profileData) || !this.profileData.length) {
        return null
      }

      if (!this.selectedTime) {
        return this.profileData[0]
      }

      return this.profileData.find(item => item.measuredAt === this.selectedTime) || this.profileData[0]
    },

    profileAxis() {
      return String(this.sensorGroupMeta?.profileAxis || 'X').toUpperCase()
    },

    isTnProfile() {
      if (this.isRailProfile) {
        return false
      }

      if (this.profileAxis === 'TN' || this.profileAxis === 'XZ') {
        return true
      }

      const nodes = Array.isArray(this.currentProfile?.nodes)
        ? this.currentProfile.nodes
        : []

      return nodes.some(node => String(node.axisType || '').toUpperCase() === 'TN')
    },

    chartTitle() {
      if (this.isRailProfile) return '레일변위계 프로파일'

      return this.isTnProfile
        ? '내공변위계 X-Z 프로파일'
        : '지중경사계 프로파일'
    },

    profileDescription() {
      if (this.isRailProfile) {
        return '동일 시각의 레일변위계 노드 변위를 거리 기준으로 표시합니다.'
      }

      return this.isTnProfile
        ? '동일 시각의 TN 노드 A축 각도값으로 초기선과 현재선을 각각 X-Z 좌표로 누적하여 터널 단면 형상으로 표시합니다.'
        : '동일 시각의 센서 노드 값을 depth 기준으로 표시합니다.'
    },

    chartMin() {
      const value = Number(this.sensorGroupMeta?.chartMin)
      return Number.isFinite(value) ? value : undefined
    },

    chartMax() {
      const value = Number(this.sensorGroupMeta?.chartMax)
      return Number.isFinite(value) ? value : undefined
    },

    thresholdLines() {
      if (this.isTnProfile) {
        return []
      }

      return Array.isArray(this.sensorGroupMeta?.thresholdLines)
        ? this.sensorGroupMeta.thresholdLines
        : []
    },

    currentNodes() {
      const nodes = Array.isArray(this.currentProfile?.nodes)
        ? this.currentProfile.nodes
        : []

      return this.sortNodes(nodes)
    },

    baseNodes() {
      const nodes = Array.isArray(this.currentProfile?.baseNodes)
        ? this.currentProfile.baseNodes
        : []

      return this.sortNodes(nodes)
    },

    currentProfileNodes() {
      const nodes = Array.isArray(this.currentProfile?.currentNodes)
        ? this.currentProfile.currentNodes
        : []

      return this.sortNodes(nodes.length ? nodes : this.currentNodes)
    },

    tableNodes() {
      if (this.isTnProfile) {
        return this.currentProfileNodes.filter(node => node.sensorName !== '기준점')
      }

      if (this.isRailProfile) {
        return this.chartNodes
      }

      return this.currentNodes
    },

    minMaxSummary() {
      const nodes = this.tableNodes || []

      const values = nodes
        .map(node => Number(node.value))
        .filter(v => Number.isFinite(v))

      if (!values.length) {
        return null
      }

      return {
        min: Math.min(...values),
        max: Math.max(...values)
      }
    },

    chartNodes() {
      if (this.isTnProfile) {
        return this.currentProfileNodes
      }

      const axisNodes = this.currentNodes.filter(node =>
        String(node.axisType || '').toUpperCase() === this.profileAxis
      )

      return axisNodes.length ? axisNodes : this.currentNodes
    },

    chartIdentity() {
      const first = this.profileData?.[0]
      return [
        this.sensorGroupMeta?.sensorGroupId || this.sensorGroupMeta?.id || '',
        this.sensorGroupMeta?.sensorGroupName || '',
        this.sensorGroupMeta?.profileAxis || '',
        this.sensorGroupMeta?.multiSensorType || '',
        first?.measuredAt || '',
        this.profileData?.length || 0,
        this.isRailProfile ? 'R' : (this.isTnProfile ? 'TN' : 'TS')
      ].join('|')
    },

    isRailProfile() {
      return String(this.sensorGroupMeta?.multiSensorType || '').toUpperCase() === 'R'
    },
  },

  watch: {
    chartIdentity: {
      immediate: true,
      handler() {
        this.selectedTime = this.profileData?.[0]?.measuredAt || null
        this.resetAxisControlState()

        this.$nextTick(() => {
          this.renderChart()
        })
      }
    },

    selectedTime() {
      this.resetAxisControlState()

      this.$nextTick(() => {
        this.renderChart()
      })
    }
  },

  beforeUnmount() {
    this.destroyChart()
  },

  methods: {
    createEmptyAxisControl() {
      return {
        xMin: '',
        xMax: '',
        zMin: '',
        zMax: '',
        originalXMin: '',
        originalXMax: '',
        originalZMin: '',
        originalZMax: ''
      }
    },

    resetAxisControlState() {
      this.axisControl = this.createEmptyAxisControl()
    },

    sortNodes(nodes) {
      const safeNodes = Array.isArray(nodes) ? nodes : []

      return safeNodes
        .map(node => ({
          ...node,
          depth: node.depth ?? 0,
          axisType: node.axisType || '',
          sortOrder: node.sortOrder ?? 0
        }))
        .sort((a, b) => {
          const aSort = Number(a.sortOrder ?? 0)
          const bSort = Number(b.sortOrder ?? 0)

          if (aSort !== bSort) return aSort - bSort

          const aDepth = Number(a.depth ?? 0)
          const bDepth = Number(b.depth ?? 0)

          if (aDepth !== bDepth) return aDepth - bDepth

          const aAxis = String(a.axisType || '')
          const bAxis = String(b.axisType || '')
          return aAxis.localeCompare(bAxis)
        })
    },

    destroyChart() {
      if (this.chart && typeof this.chart.destroy === 'function') {
        this.chart.destroy()
      }

      this.chart = null
    },

    renderChart() {
      this.destroyChart()

      if (!this.currentProfile || !this.$refs.profileCanvas) {
        return
      }

      if (this.isRailProfile) {
        this.renderRailChart()
      } else if (this.isTnProfile) {
        this.renderTnChart()
      } else {
        this.renderTsChart()
      }
    },

    parseAxisValue(value, defaultValue) {
      if (value === null || value === undefined || value === '') {
        return defaultValue
      }

      const num = Number(value)
      return Number.isFinite(num) ? num : defaultValue
    },

    syncOriginalAxisControl({ xMin, xMax, zMin, zMax }) {
      this.axisControl.originalXMin = String(xMin)
      this.axisControl.originalXMax = String(xMax)
      this.axisControl.originalZMin = String(zMin)
      this.axisControl.originalZMax = String(zMax)

      this.axisControl.xMin = String(xMin)
      this.axisControl.xMax = String(xMax)
      this.axisControl.zMin = String(zMin)
      this.axisControl.zMax = String(zMax)
    },

    applyChartScale() {
      if (!this.chart) return

      const xMin = this.parseAxisValue(this.axisControl.xMin, this.chart.options.scales.x.min)
      const xMax = this.parseAxisValue(this.axisControl.xMax, this.chart.options.scales.x.max)
      const zMin = this.parseAxisValue(this.axisControl.zMin, this.chart.options.scales.y.min)
      const zMax = this.parseAxisValue(this.axisControl.zMax, this.chart.options.scales.y.max)

      this.chart.options.scales.x.min = xMin
      this.chart.options.scales.x.max = xMax
      this.chart.options.scales.y.min = zMin
      this.chart.options.scales.y.max = zMax

      this.chart.update('none')
    },

    resetChartScale() {
      this.axisControl.xMin = this.axisControl.originalXMin
      this.axisControl.xMax = this.axisControl.originalXMax
      this.axisControl.zMin = this.axisControl.originalZMin
      this.axisControl.zMax = this.axisControl.originalZMax

      this.applyChartScale()
    },

    buildThresholdDatasets(direction = 'vertical') {
      const thresholdGroup = {
        level1: [],
        level2: [],
        level3: []
      }

      this.thresholdLines.forEach(line => {
        const value = Number(line.value)

        if (!Number.isFinite(value)) return

        if (String(line.colorKey || '').includes('level1')) {
          thresholdGroup.level1.push(value)
        }

        if (String(line.colorKey || '').includes('level2')) {
          thresholdGroup.level2.push(value)
        }

        if (String(line.colorKey || '').includes('level3')) {
          thresholdGroup.level3.push(value)
        }
      })

      const datasets = []

      const appendThresholdDatasets = (levelKey, label, color) => {
        const values = thresholdGroup[levelKey]
        if (!values.length) return

        values.forEach((value, index) => {
          datasets.push({
            label: index === 0
              ? `${label}(${values.join(', ')})`
              : `${label} hidden`,
            data: [],
            borderColor: color,
            backgroundColor: color,
            borderDash: [6, 4],
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 0,
            customType: 'threshold',
            thresholdGroup: levelKey,
            thresholdValue: value,
            thresholdDirection: direction,
            hiddenLegend: index !== 0
          })
        })
      }

      appendThresholdDatasets('level1', '1차관리기준치', '#14b8a6')
      appendThresholdDatasets('level2', '2차관리기준치', '#f59e0b')
      appendThresholdDatasets('level3', '3차관리기준치', '#ef4444')

      return datasets
    },

    getLegendOptions() {
      return {
        display: true,
        position: 'top',
        labels: {
          filter(item, chartData) {
            const dataset = chartData.datasets[item.datasetIndex]
            return !dataset?.hiddenLegend
          }
        },
        onClick(e, legendItem, legend) {
          const chart = legend.chart
          const clickedDataset = chart.data.datasets[legendItem.datasetIndex]

          if (!clickedDataset) return

          if (clickedDataset.customType === 'threshold') {
            const groupKey = clickedDataset.thresholdGroup

            const groupIndexes = chart.data.datasets
              .map((dataset, index) => ({ dataset, index }))
              .filter(item =>
                item.dataset.customType === 'threshold' &&
                item.dataset.thresholdGroup === groupKey
              )
              .map(item => item.index)

            const shouldHide = groupIndexes.some(index =>
              chart.isDatasetVisible(index)
            )

            groupIndexes.forEach(index => {
              chart.setDatasetVisibility(index, !shouldHide)
            })

            chart.update()
            return
          }

          const index = legendItem.datasetIndex
          chart.setDatasetVisibility(index, !chart.isDatasetVisible(index))
          chart.update()
        }
      }
    },

    renderTsChart() {
      const canvas = this.$refs.profileCanvas
      const nodes = this.chartNodes

      if (!canvas || !nodes.length) return

      const points = nodes
        .filter(node => Number.isFinite(Number(node.value)) && Number.isFinite(Number(node.depth)))
        .map(node => ({
          x: Number(node.value),
          y: Number(node.depth)
        }))

      if (!points.length) return

      const xValues = points.map(point => point.x)
      const yValues = points.map(point => point.y)

      const minX = this.chartMin ?? Math.min(...xValues, -10)
      const maxX = this.chartMax ?? Math.max(...xValues, 10)
      const minY = Math.min(...yValues)
      const maxY = Math.max(...yValues)

      this.syncOriginalAxisControl({
        xMin: minX,
        xMax: maxX,
        zMin: minY,
        zMax: maxY
      })

      const ctx = canvas.getContext('2d')

      this.chart = markRaw(new Chart(ctx, {
        type: 'line',
        data: {
          datasets: [
            {
              label: `${this.profileAxis} 현재값`,
              data: points,
              parsing: false,

              borderColor: '#3b82f6',
              backgroundColor: '#3b82f6',

              borderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 5,
              pointBackgroundColor: '#3b82f6',
              pointBorderColor: '#3b82f6',

              tension: 0,
              fill: false
            },

            ...this.buildThresholdDatasets('vertical')
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: this.getLegendOptions(),
            tooltip: {
              callbacks: {
                label: context => {
                  const point = context.raw || {}
                  return `변화량: ${this.formatNumber(point.x)}, depth: ${this.formatNumber(point.y)}`
                }
              }
            }
          },
          scales: {
            x: {
              type: 'linear',
              min: this.parseAxisValue(this.axisControl.xMin, minX),
              max: this.parseAxisValue(this.axisControl.xMax, maxX),
              title: {
                display: true,
                text: '변화량'
              }
            },
            y: {
              type: 'linear',
              reverse: true,
              min: this.parseAxisValue(this.axisControl.zMin, minY),
              max: this.parseAxisValue(this.axisControl.zMax, maxY),
              title: {
                display: true,
                text: 'Depth'
              }
            }
          }
        },
        plugins: [thresholdLinePlugin]
      }))
    },

    renderRailChart() {
      const canvas = this.$refs.profileCanvas
      const nodes = this.chartNodes

      if (!canvas || !nodes.length) return

      /*
      * 레일변위계(R)
      * - X축: 거리(m) = node.depth
      * - Y축: 변위(mm) = node.value
      * - 지중경사계처럼 누적하지 않고 각 지점 독립값을 그대로 표시한다.
      */
      const points = nodes
        .filter(node => Number.isFinite(Number(node.value)) && Number.isFinite(Number(node.depth)))
        .map(node => ({
          x: Number(node.depth),
          y: Number(node.value)
        }))

      if (!points.length) return

      const xValues = points.map(point => point.x)
      const yValues = points.map(point => point.y)

      const minX = Math.min(...xValues)
      const maxX = Math.max(...xValues)

      const minY = this.chartMin ?? Math.min(...yValues, -10)
      const maxY = this.chartMax ?? Math.max(...yValues, 10)

      this.syncOriginalAxisControl({
        xMin: minX,
        xMax: maxX,
        zMin: minY,
        zMax: maxY
      })

      const ctx = canvas.getContext('2d')

      this.chart = markRaw(new Chart(ctx, {
        type: 'line',
        data: {
          datasets: [
            {
              label: `${this.profileAxis}축 변위(mm)`,
              data: points,
              parsing: false,
              borderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 5,
              tension: 0,
              fill: false
            },

            ...this.buildThresholdDatasets('horizontal')
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: this.getLegendOptions(),
            tooltip: {
              callbacks: {
                label: context => {
                  const point = context.raw || {}
                  return `거리: ${this.formatNumber(point.x)}m, 변위: ${this.formatNumber(point.y)}mm`
                }
              }
            }
          },
          scales: {
            x: {
              type: 'linear',
              min: this.parseAxisValue(this.axisControl.xMin, minX),
              max: this.parseAxisValue(this.axisControl.xMax, maxX),
              title: {
                display: true,
                text: '거리(m)'
              }
            },
            y: {
              type: 'linear',
              min: this.parseAxisValue(this.axisControl.zMin, minY),
              max: this.parseAxisValue(this.axisControl.zMax, maxY),
              title: {
                display: true,
                text: '변위(mm)'
              }
            }
          }
        },
        plugins: [thresholdLinePlugin]
      }))
    },

    renderTnChart() {
      const canvas = this.$refs.profileCanvas
      if (!canvas) return

      /*
       * TN 그래프는 X/Z 원 좌표값을 그대로 사용한다.
       * 그래프 표시 비율은 CSS에서 chart-wrap 높이를 크게 잡고,
       * chart-card 내부 스크롤로 확인하는 방식으로 처리한다.
       */
      const basePoints = this.baseNodes
        .filter(node => Number.isFinite(Number(node.x)) && Number.isFinite(Number(node.z)))
        .map(node => ({
          x: Number(node.x),
          y: Number(node.z)
        }))

      const currentPoints = this.currentProfileNodes
        .filter(node => Number.isFinite(Number(node.x)) && Number.isFinite(Number(node.z)))
        .map(node => ({
          x: Number(node.x),
          y: Number(node.z)
        }))

      if (!currentPoints.length) return

      const allPoints = [...basePoints, ...currentPoints]
      const xValues = allPoints.map(point => point.x)
      const yValues = allPoints.map(point => point.y)

      const minX = Math.min(...xValues)
      const maxX = Math.max(...xValues)
      const minY = Math.min(...yValues)
      const maxY = Math.max(...yValues)

      const xPadding = Math.max((maxX - minX) * 0.15, 100)
      const yPadding = Math.max((maxY - minY) * 0.08, 100)

      const defaultXMin = Number((minX - xPadding).toFixed(1))
      const defaultXMax = Number((maxX + xPadding).toFixed(1))
      const defaultZMin = Number((minY - yPadding).toFixed(1))
      const defaultZMax = Number((maxY + yPadding).toFixed(1))

      this.syncOriginalAxisControl({
        xMin: defaultXMin,
        xMax: defaultXMax,
        zMin: defaultZMin,
        zMax: defaultZMax
      })

      const ctx = canvas.getContext('2d')

      this.chart = markRaw(new Chart(ctx, {
        type: 'line',
        data: {
          datasets: [
            {
              label: '초기 형상',
              data: basePoints,
              parsing: false,
              borderWidth: 2,
              pointRadius: 3,
              pointHoverRadius: 5,
              tension: 0,
              fill: false
            },
            {
              label: '현재 형상',
              data: currentPoints,
              parsing: false,
              borderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6,
              tension: 0,
              fill: false
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: {
              display: true,
              position: 'top'
            },
            tooltip: {
              callbacks: {
                label: context => {
                  const point = context.raw || {}
                  return `X: ${this.formatNumber(point.x)}mm, Z: ${this.formatNumber(point.y)}mm`
                }
              }
            }
          },
          scales: {
            x: {
              type: 'linear',
              min: this.parseAxisValue(this.axisControl.xMin, defaultXMin),
              max: this.parseAxisValue(this.axisControl.xMax, defaultXMax),
              title: {
                display: true,
                text: 'X 좌표(mm)'
              }
            },
            y: {
              type: 'linear',
              reverse: true,
              min: this.parseAxisValue(this.axisControl.zMin, defaultZMin),
              max: this.parseAxisValue(this.axisControl.zMax, defaultZMax),
              title: {
                display: true,
                text: 'Z 좌표(mm)'
              }
            }
          }
        }
      }))
    },

    formatNumber(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '0.0000'
      return num.toFixed(4)
    },

    formatTime(value) {
      if (!value) return '-'

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return value
      }

      const yy = String(date.getFullYear()).slice(-2)
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')
      const ss = String(date.getSeconds()).padStart(2, '0')

      return `${yy}-${mm}-${dd} ${hh}:${mi}:${ss}`
    }
  }
}
</script>

<style scoped>
.multi-profile-body {
  flex: 1;
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.profile-area {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #dbe3ef;
  border-radius: 10px;
  padding: 12px;
  box-sizing: border-box;
}

.profile-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.profile-header-left {
  min-width: 0;
}

.profile-category {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.profile-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

.profile-sub {
  font-size: 12px;
  color: #64748b;
}

.profile-header-right {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.axis-control-box {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
}

.axis-label {
  font-size: 12px;
  font-weight: 700;
  color: #334155;
}

.axis-input {
  width: 80px;
  height: 32px;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  padding: 0 8px;
  font-size: 12px;
  outline: none;
  box-sizing: border-box;
}

.axis-input:focus {
  border-color: #3b82f6;
}

.axis-tilde {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}

.axis-btn {
  height: 32px;
  padding: 0 10px;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  background: #f8fafc;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.axis-btn:hover {
  background: #eef2f7;
}

.time-select {
  height: 34px;
  min-width: 160px;
  border: 1px solid #cfd8e3;
  border-radius: 6px;
  padding: 0 8px;
  background: #fff;
  font-size: 12px;
}

.profile-empty {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 13px;
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
}

.chart-card,
.table-card {
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #ffffff;
  padding: 10px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.chart-card {
  height: auto;
  overflow: visible;
}

.chart-title,
.table-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
}

.chart-wrap {
  height: 380px;
  min-height: 380px;
}

/* ========================================
   TN(내공변위계) 전용
   고객사 요구사항:
   뒤집힌 U 형상이 눌리지 않게 높이 확대
======================================== */
.tn-chart-wrap {
  height: 620px;
  min-height: 620px;
}

.chart-wrap canvas {
  width: 100% !important;
  height: 100% !important;
}

.table-card {
  min-height: 0;
}

.table-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.table-title-row .table-title {
  margin-bottom: 0;
}

.minmax-summary {
  font-size: 13px;
  color: #374151;
  font-weight: 500;
  white-space: nowrap;
}

.minmax-summary strong {
  margin: 0 4px;
  font-weight: 700;
  color: #111827;
}

.table-scroll {
  width: 100%;
  overflow: auto;
}

.profile-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.profile-table th,
.profile-table td {
  border: 1px solid #dbe3ef;
  padding: 7px 8px;
  text-align: center;
  white-space: nowrap;
}

.profile-table th {
  background: #f1f5f9;
  font-weight: 700;
  color: #0f172a;
  position: sticky;
  top: 0;
  z-index: 2;
}

.profile-table td {
  color: #111827;
}

@media (max-width: 1400px) {
  .profile-header {
    flex-direction: column;
    align-items: stretch;
  }

  .profile-header-right {
    align-items: flex-start;
  }

  .axis-control-box {
    justify-content: flex-start;
  }
}

@media (max-width: 1100px) {
  .chart-wrap {
    height: 340px;
    min-height: 340px;
  }

  .tn-chart-wrap {
    height: 520px;
    min-height: 520px;
  }
}

/* =========================
   INTERACTION
========================= */

.axis-btn {
  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    color 0.18s ease,
    transform 0.12s ease,
    box-shadow 0.18s ease;
}

.axis-btn:hover {
  background: #2563eb;
  border-color: #2563eb;
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.22);
}

.axis-btn:active {
  transform: translateY(0);
}

.profile-table tbody tr {
  transition: all 0.15s ease;
}

.profile-table tbody tr:hover td {
  background: #eff6ff;
}

.profile-table tbody td:first-child {
  border-left: 3px solid transparent;
}

.profile-table tbody tr:hover td:first-child {
  border-left-color: #3b82f6;
}
</style>